import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import {
  Search, MapPin, Phone, Clock, Navigation, MessageCircle,
  List, Map as MapIcon, ChevronLeft, Loader2, Package, AlertTriangle, FileText, Bell
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";
import { useAuth } from "../App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

const pharmacyIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const clinicIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const userIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const MapCenterHandler = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  return null;
};

const PharmacyCard = ({ result, onClick }) => {
  const { t } = useTranslation("public");
  const { pharmacy, stock, distance, is_open } = result;

  const openDirections = () => {
    if (pharmacy.latitude && pharmacy.longitude) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${pharmacy.latitude},${pharmacy.longitude}`,
        "_blank"
      );
    } else if (pharmacy.address && pharmacy.city) {
      const address = encodeURIComponent(`${pharmacy.address}, ${pharmacy.commune || ""}, ${pharmacy.city}, RDC`);
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${address}`,
        "_blank"
      );
    }
  };

  const handleCall = (e) => {
    e.stopPropagation();
    window.location.href = `tel:${pharmacy.phone}`;
  };

  const handleWhatsApp = (e) => {
    e.stopPropagation();
    const phone = pharmacy.whatsapp || pharmacy.phone;
    window.open(`https://wa.me/${phone.replace(/[^0-9]/g, "")}`, "_blank");
  };

  const handleDirections = (e) => {
    e.stopPropagation();
    openDirections();
  };

  const handleAddressClick = (e) => {
    e.stopPropagation();
    openDirections();
  };

  return (
    <Card
      className="border-0 shadow-soft card-hover cursor-pointer stagger-item"
      onClick={onClick}
      data-testid={`pharmacy-card-${pharmacy.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {pharmacy.photo_url && (
            <img
              src={pharmacy.photo_url}
              alt={pharmacy.entity_name}
              className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
            />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-semibold text-slate-900">
                {pharmacy.entity_name}
              </h3>
              <Badge
                className={`text-xs ${
                  is_open
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {is_open ? t("open") : t("closed")}
              </Badge>
            </div>
            <p
              className="text-sm text-omu-blue-500 mt-1 cursor-pointer hover:underline"
              onClick={handleAddressClick}
              data-testid={`address-link-${pharmacy.id}`}
            >
              <MapPin className="inline h-4 w-4 mr-1" />
              {pharmacy.address}
              {pharmacy.commune && `, ${pharmacy.commune}`}
              , {pharmacy.city}
            </p>
            {distance && (
              <p className="text-sm text-slate-500 font-medium mt-1">
                {t("distanceKm", { distance })}
              </p>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {stock.slice(0, 3).map((item, idx) => (
            <Badge
              key={idx}
              variant="outline"
              className={`text-xs ${item.medicine.requires_prescription ? 'border-red-300 bg-red-50 text-red-700' : ''}`}
            >
              {item.medicine.requires_prescription && (
                <FileText className="h-3 w-3 mr-1 inline" />
              )}
              {item.medicine.name}: {t("units", { count: item.quantity })}
              {item.price && ` - ${item.price} CDF`}
            </Badge>
          ))}
          {stock.length > 3 && (
            <Badge variant="outline" className="text-xs">
              {t("othersCount", { count: stock.length - 3 })}
            </Badge>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 rounded-full"
            onClick={handleCall}
            data-testid={`call-btn-${pharmacy.id}`}
          >
            <Phone className="h-4 w-4 mr-1" />
            {t("call")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 rounded-full"
            onClick={handleWhatsApp}
            data-testid={`whatsapp-btn-${pharmacy.id}`}
          >
            <MessageCircle className="h-4 w-4 mr-1" />
            {t("whatsapp")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={handleDirections}
            data-testid={`directions-btn-${pharmacy.id}`}
          >
            <Navigation className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation("public");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [medicinesFound, setMedicinesFound] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list");
  const [userLocation, setUserLocation] = useState(null);
  const [radiusFilter, setRadiusFilter] = useState("all");
  const [alertSent, setAlertSent] = useState(false);

  const handleCreateAlert = async () => {
    if (!user) {
      toast.error(t("loginForAlerts"));
      return;
    }
    try {
      await apiClient.post("/alerts/medicine", { medicine_name: query });
      setAlertSent(true);
      toast.success(t("alertCreated"));
    } catch (error) {
      toastApiError(error);
    }
  };

  useEffect(() => {
    const q = searchParams.get("q");
    const lat = searchParams.get("lat");
    const lng = searchParams.get("lng");
    const city = searchParams.get("city");

    if (lat && lng) {
      setUserLocation({ lat: parseFloat(lat), lng: parseFloat(lng) });
    } else {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const loc = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            setUserLocation(loc);
            if (q) {
              performSearch(q, loc.lat, loc.lng, city);
            }
          },
          () => {}
        );
      }
    }

    if (q) {
      performSearch(q, lat ? parseFloat(lat) : null, lng ? parseFloat(lng) : null, city);
    }
  }, []);

  useEffect(() => {
    if (radiusFilter === "all") {
      setFilteredResults(results);
    } else {
      const maxDistance = parseFloat(radiusFilter);
      setFilteredResults(
        results.filter(r => {
          if (!r.distance && r.distance !== 0) return true;
          const dist = parseFloat(r.distance);
          return !isNaN(dist) && dist <= maxDistance;
        })
      );
    }
  }, [radiusFilter, results]);

  const performSearch = async (searchQuery, lat, lng, city) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await apiClient.post("/search", {
        query: searchQuery,
        latitude: lat,
        longitude: lng,
        city: city
      });

      setResults(response.data.results);
      setFilteredResults(response.data.results);
      setMedicinesFound(response.data.medicines_found);

      if (response.data.results.length === 0) {
        toast.info(t("noPharmacyFound"));
      }
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.set("q", query);
    if (userLocation) {
      params.set("lat", userLocation.lat);
      params.set("lng", userLocation.lng);
    }
    setSearchParams(params);
    performSearch(query, userLocation?.lat, userLocation?.lng, null);
  };

  const handlePharmacyClick = (pharmacyId) => {
    const searchQuery = searchParams.get("q") || "";
    if (searchQuery) {
      navigate(`/pharmacy/${pharmacyId}?q=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate(`/pharmacy/${pharmacyId}`);
    }
  };

  const mapCenter = userLocation
    ? [userLocation.lat, userLocation.lng]
    : results.length > 0 && results[0].pharmacy.latitude
    ? [results[0].pharmacy.latitude, results[0].pharmacy.longitude]
    : [-4.325, 15.322];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              data-testid="back-btn"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>

            <Link to="/">
              <img src={LOGO_URL} alt="OMU" className="h-16 w-auto" />
            </Link>

            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder={t("searchMedicinePlaceholder")}
                  className="pl-10 h-10 rounded-full border-slate-200"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  data-testid="search-input"
                />
              </div>
              <Button
                className="h-10 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
                onClick={handleSearch}
                data-testid="search-submit-btn"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-amber-50 border-b border-amber-200 py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          <span>{t("searchDisclaimer")}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            {medicinesFound.length > 0 && (
              <p className="text-sm text-slate-500">
                {t("medicinesFoundSummary", { medicines: medicinesFound.length, pharmacies: filteredResults.length })}
                {radiusFilter !== "all" && t("withinRadius", { radius: radiusFilter })}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
              <span className="text-xs text-slate-500 px-2">{t("radius")}</span>
              {["all", "1", "5", "10", "20"].map((radius) => (
                <Button
                  key={radius}
                  size="sm"
                  variant={radiusFilter === radius ? "default" : "ghost"}
                  className={`h-7 px-2 rounded-full text-xs ${
                    radiusFilter === radius ? "bg-omu-blue-500" : ""
                  }`}
                  onClick={() => setRadiusFilter(radius)}
                  data-testid={`radius-filter-${radius}`}
                >
                  {radius === "all" ? t("radiusAll") : `${radius}km`}
                </Button>
              ))}
            </div>

            <Tabs value={view} onValueChange={setView}>
              <TabsList className="bg-slate-100">
                <TabsTrigger value="list" data-testid="view-list-btn">
                  <List className="h-4 w-4 mr-1" />
                  {t("list")}
                </TabsTrigger>
                <TabsTrigger value="map" data-testid="view-map-btn">
                  <MapIcon className="h-4 w-4 mr-1" />
                  {t("map")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-omu-blue-500" />
          </div>
        ) : (
          <>
            {view === "list" && (
              <div className="space-y-4">
                {filteredResults.length === 0 ? (
                  <Card className="border-0 shadow-soft">
                    <CardContent className="py-12 text-center">
                      <Package className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="font-heading text-lg font-semibold text-slate-900">
                        {t("noResults")}
                      </h3>
                      <p className="text-slate-500 mt-2">
                        {radiusFilter !== "all"
                          ? t("noResultsInRadius", { radius: radiusFilter })
                          : t("noPharmacyInStock")
                        }
                      </p>
                      {user && user.role === "client" && !alertSent && (
                        <Button
                          className="mt-4 bg-omu-blue-500 hover:bg-omu-blue-600 rounded-full"
                          onClick={handleCreateAlert}
                          data-testid="notify-me-btn"
                        >
                          <Bell className="h-4 w-4 mr-2" />
                          {t("notifyWhenAvailable")}
                        </Button>
                      )}
                      {alertSent && (
                        <p className="mt-4 text-green-600 text-sm font-medium">
                          {t("alertActive")}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  filteredResults.map((result) => (
                    <PharmacyCard
                      key={result.pharmacy.id}
                      result={result}
                      onClick={() => handlePharmacyClick(result.pharmacy.id)}
                    />
                  ))
                )}
              </div>
            )}

            {view === "map" && (
              <div className="h-[calc(100vh-250px)] rounded-2xl overflow-hidden shadow-soft relative">
                {filteredResults.filter(r => r.pharmacy.latitude && r.pharmacy.longitude).length === 0 && (
                  <div className="absolute inset-0 z-[1000] bg-white/90 flex items-center justify-center">
                    <div className="text-center p-6">
                      <MapPin className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="font-heading text-lg font-semibold text-slate-900">
                        {t("gpsUnavailableTitle")}
                      </h3>
                      <p className="text-slate-500 mt-2 max-w-sm">
                        {t("gpsUnavailableDesc")}
                      </p>
                    </div>
                  </div>
                )}
                <MapContainer
                  center={mapCenter}
                  zoom={13}
                  className="h-full w-full"
                  data-testid="results-map"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapCenterHandler center={mapCenter} />

                  {userLocation && (
                    <Marker
                      position={[userLocation.lat, userLocation.lng]}
                      icon={userIcon}
                    >
                      <Popup>{t("yourPosition")}</Popup>
                    </Marker>
                  )}

                  {filteredResults.map(
                    (result) =>
                      result.pharmacy.latitude &&
                      result.pharmacy.longitude && (
                        <Marker
                          key={result.pharmacy.id}
                          position={[
                            result.pharmacy.latitude,
                            result.pharmacy.longitude
                          ]}
                          icon={
                            result.pharmacy.entity_type === "pharmacie"
                              ? pharmacyIcon
                              : clinicIcon
                          }
                        >
                          <Popup>
                            <div className="min-w-[200px]">
                              <h3 className="font-semibold">
                                {result.pharmacy.entity_name}
                              </h3>
                              <p className="text-sm text-slate-500">
                                {result.pharmacy.address}
                              </p>
                              <Badge
                                className={`mt-2 ${
                                  result.is_open
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {result.is_open ? t("open") : t("closed")}
                              </Badge>
                              <div className="mt-2">
                                <Button
                                  size="sm"
                                  className="w-full rounded-full bg-omu-blue-500"
                                  onClick={() =>
                                    handlePharmacyClick(result.pharmacy.id)
                                  }
                                >
                                  {t("viewDetails")}
                                </Button>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      )
                  )}
                </MapContainer>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchResultsPage;
